import { useCallback, useMemo } from "react";
import { getColumnLayout, resolveColumnLayoutId } from "@/domain/columnLayouts";
import {
  matchesChipFilter,
  matchesRegion,
  matchesSearch,
  type FilterContext,
} from "@/domain/filters";
import { showsAllFiltersByDefault } from "@/domain/permissions";
import type { ColumnDefinition, ColumnLayout, Intervention, View } from "@/domain/types";
import { getViewById } from "@/domain/views";
import { useApp, useUi } from "./AppContext";

export function useSelectedView(): View {
  const { data } = useApp();
  const { viewId } = useUi();
  return useMemo(() => getViewById(data.settings.views, viewId), [data.settings.views, viewId]);
}

export interface ActiveColumns {
  layoutId: string;
  layout: ColumnLayout | null;
  columns: ColumnDefinition[];
}

export function useActiveColumns(): ActiveColumns {
  const { data } = useApp();
  const { columnLayoutOverride } = useUi();
  const view = useSelectedView();
  return useMemo(() => {
    const layoutId = resolveColumnLayoutId(view, columnLayoutOverride);
    return {
      layoutId,
      layout: getColumnLayout(layoutId),
      columns: data.dataset.columnDefsByLayout[layoutId] ?? [],
    };
  }, [view, columnLayoutOverride, data.dataset.columnDefsByLayout]);
}

export function useFilterContext(): FilterContext {
  const { authMode, data, user } = useApp();
  const ui = useUi();
  const view = useSelectedView();
  const { availableViewCodes, objectives } = data.dataset;
  return useMemo(
    () => ({
      view,
      availableViewCodes,
      availableObjectives: new Set(Object.keys(objectives)),
      chipSelections: ui.chipSelections,
      statsFiltersHidden: ui.tab === "stats" && !ui.statsFiltersVisible,
      showAllByDefault: authMode === "individual" && showsAllFiltersByDefault(user),
    }),
    [
      view,
      availableViewCodes,
      objectives,
      ui.chipSelections,
      ui.tab,
      ui.statsFiltersVisible,
      authMode,
      user,
    ],
  );
}

/**
 * Predicate for the location, chip and (optionally) search filters, as
 * applied by the matrix, the list and the statistics.
 */
export function useVisibilityFilter({
  includeSearch,
}: {
  includeSearch: boolean;
}): (item: Intervention) => boolean {
  const { selectedRegions, search } = useUi();
  const context = useFilterContext();
  return useCallback(
    (item: Intervention) =>
      matchesRegion(item, selectedRegions) &&
      matchesChipFilter(item, context) &&
      (!includeSearch || matchesSearch(item, search)),
    [selectedRegions, context, includeSearch, search],
  );
}

/** "All locations", the single selected location, or "3 locations selected". */
export function formatRegionSelection(
  selected: ReadonlySet<string>,
  allLabel: string,
  manyLabel: string,
): string {
  if (selected.size === 0) return allLabel;
  const [first] = selected;
  return selected.size === 1 && first ? first : `${selected.size} ${manyLabel}`;
}
